import { useState, useContext, useEffect } from 'react';

import Card from '../../bootstrap/Card';
import PaginationButtons, { PER_COUNT } from '../../PaginationButtons';

import { TokenAccessLog } from './token-access-log/token-access-log';
import { CreateOrEditUserForm } from './edit-user-form';
import { UserContext } from '../../../contexts/user-context';
import { TokensTable } from './token-table/tokens-table';
import { useTokensIssuedByUserAuthorizationTypes } from '../../../hooks/entrada-segura/use-tokens-issued-by-user-authorization-types';
import { Token, TokenList } from '../../../type/token';
import { ShowAccessesFromUser } from './show-accesses-from-user';
import Input from '../../bootstrap/forms/Input';
import { EditTokenForm } from './edit-token-form';

export const ConsultComponents = ({ isFluid = true }) => {
	const [shouldOpenEditUserForm, setShouldOpenEditUserForm] = useState(false);
	const [shouldOpenSeeAccess, setShouldOpenSeeAccess] = useState(false);
	const [shouldOpenTokenLog, setShouldOpenTokenLog] = useState(false);
	const [shouldOpenEditTokenForm, setShouldOpenEditTokenForm] = useState(false);
	const [searchString, setSearchString] = useState('');

	const [userToEdit, setUserToEdit] = useState(0);
	const [tokenToShowLog, setTokenToShowLog] = useState('');
	const [tokenToEdit, setTokenToEdit] = useState<Token | null>(null);

	const handleUpcomingEdit = (userId: number) => {
		setUserToEdit(userId);
		setShouldOpenEditUserForm(!shouldOpenEditUserForm);
	};
	const handleShowTokenLog = (tokenId: string) => {
		setTokenToShowLog(tokenId);
		setShouldOpenTokenLog(!shouldOpenTokenLog);
	};

	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(PER_COUNT['10']);
	const [tokens, setTokens] = useState<TokenList>();
	const [groupedUserTokens, setGroupedUserTokens] = useState<Map<number, Token[]>>(new Map());
	const [totalUsers, setTotalUsers] = useState(0);
	const [refreshTable, setRefreshTable] = useState<boolean>(false);
	const [isFetching, setIsFetching] = useState<boolean>(true);

	const { user } = useContext(UserContext);
	const activeUserId = user.id;
	const authorizationTypes = ["grant", "checkin"];
	const getTokens = useTokensIssuedByUserAuthorizationTypes({ 
		userId: activeUserId, 
		isSuperUser: user.isSuperUser, 
		perPage: 10000, 
		page: 1, 
		authorizationTypes: authorizationTypes, 
		refreshTable, 
		setIsFetching,
		searchUserRequestedName: searchString
	})

	useEffect(() => {
		setRefreshTable(false);
		if (getTokens) {
			const sortedTokens = getTokens.results.sort((a, b) => {
				if (!a.expiresAt) return 1;
				if (!b.expiresAt) return -1;
				return new Date(b.expiresAt).getTime() - new Date(a.expiresAt).getTime();
			});

			const grouped = new Map<number, Token[]>();
			sortedTokens.forEach(token => {
				const userId = token.userRequested.id;
				if (!grouped.has(userId)) {
					grouped.set(userId, []);
				}
				grouped.get(userId)!.push(token);
			});

			const userIds = Array.from(grouped.keys());
			setTotalUsers(userIds.length);

			const startIndex = (currentPage - 1) * perPage;
			const endIndex = startIndex + perPage;
			const paginatedUserIds = userIds.slice(startIndex, endIndex);

			const paginatedGrouped = new Map<number, Token[]>();
			paginatedUserIds.forEach(userId => {
				paginatedGrouped.set(userId, grouped.get(userId)!);
			});

			setGroupedUserTokens(paginatedGrouped);

			const paginatedTokens: Token[] = [];
			paginatedUserIds.forEach(userId => {
				paginatedTokens.push(...grouped.get(userId)!);
			});

			setTokens({
				results: paginatedTokens,
				pageNumber: currentPage,
				pageSize: perPage,
				totalRecords: sortedTokens.length
			});
		}
	}, [getTokens, currentPage, perPage]);

	return (
		<section className='d-flex flex-column'>
			{isFetching ?
				<p>Carregando...</p>
				:
				<>
					<div className='fw-bold fs-5' style={{ marginLeft: '15px' }}>
						Tipos de Autorização
					</div>

					<form 
						className='d-flex my-3 align-items-center'
						onSubmit={(e) => {
							e.preventDefault()
							setCurrentPage(1)
							setRefreshTable((prev) => !prev)
						}}
					>
						<Input
							className='search-input'
							onChange={(e: any) => setSearchString(e.target.value)}
							type='text'
							placeholder='Pesquisar...'
							style={{
								display: 'flex',
								justifyContent: 'center',
								maxWidth: '200px',
								backgroundColor: 'transparent!important',
								borderColor: 'transparent!important',
							}}
							value={searchString}
						/>

						<div className='d-flex justify-content-center align-items-center'>
							<button
								className='btn btn-primary'
								onClick={() => {
									setCurrentPage(1)
									setRefreshTable((prev) => !prev)
								}}
							>
								Pesquisar
							</button>
						</div>
					</form>

					<Card stretch={isFluid}>
						<TokensTable
							setShouldOpenEditUserForm={setShouldOpenEditUserForm}
							setShouldOpenSeeAccess={setShouldOpenSeeAccess}
							setShouldOpenEditTokenForm={setShouldOpenEditTokenForm}
							setTokenToEdit={setTokenToEdit}
							setUserToEdit={setUserToEdit}
							tokens={tokens?.results}
							isFluid={isFluid}
							setRefreshTable={setRefreshTable}
							handleUpcomingEdit={handleUpcomingEdit} 
						/>
						<PaginationButtons
							data={Array.from(groupedUserTokens.keys())}
							totalResources={totalUsers}
							label='Usuários'
							setCurrentPage={setCurrentPage}
							currentPage={currentPage}
							perPage={perPage}
							setPerPage={setPerPage}
						/>
					</Card>
					<CreateOrEditUserForm
						key={`edit-user-component-${userToEdit}-${currentPage}`}
						userId={userToEdit}
						setRefreshTable={setRefreshTable}
						setShouldOpenEditUserForm={setShouldOpenEditUserForm}
						shouldOpenEditUserForm={shouldOpenEditUserForm}
					/>
					<TokenAccessLog
						tokenId={tokenToShowLog}
						setShouldOpenTokenLog={setShouldOpenTokenLog}
						shouldOpenTokenLog={shouldOpenTokenLog}
					/>
					<ShowAccessesFromUser
                    	key={`show-accesses-component-${userToEdit}`}
                    	userId={userToEdit}
                    	setIsOpen={setShouldOpenSeeAccess}
                    	isOpen={shouldOpenSeeAccess}
                	/>
					<EditTokenForm
						token={tokenToEdit}
						setIsOpenEditTokenForm={setShouldOpenEditTokenForm}
						isOpenEditTokenForm={shouldOpenEditTokenForm}
						setRefreshTable={setRefreshTable}
					/>
				</>
			}
		</section>
	);
};